import React, { useState } from 'react';
import { Box, Typography, IconButton, Tooltip, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination } from '@mui/material';
import { RestartAlt as RestartAltIcon } from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useChartConfig } from './ChartConfig';

const ProductionCharts = ({ 
  productionData, 
  onSortProductionMonthly,
  productionMonthlySortField, 
  productionMonthlySortDirection,
  onResetProductionMonthly,
  hiddenProductionLines,
  setHiddenProductionLines,
  resetHiddenProductionLines,
  hiddenFlaringLines,
  setHiddenFlaringLines,
  resetHiddenFlaringLines,
  hiddenFuelGasLines,
  setHiddenFuelGasLines,
  resetHiddenFuelGasLines
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const chartConfig = useChartConfig();

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Generate production trends data
  const generateProductionTrendsData = (productionData) => {
    const monthlyData = {};
    
    productionData.forEach(record => {
      const month = record.production_month;
      const daysInMonth = parseFloat(record.days_month) || 1;
      
      if (!monthlyData[month]) {
        monthlyData[month] = {
          month: month,
          totalOil: 0,
          totalGas: 0,
          totalDays: 0
        };
      }
      
      monthlyData[month].totalOil += parseFloat(record.oil_bbl) || 0;
      monthlyData[month].totalGas += parseFloat(record.gas_mcf) || 0;
      monthlyData[month].totalDays = daysInMonth;
    });
    
    return Object.values(monthlyData)
      .map(data => ({
        month: data.month ? data.month.substring(0, 7) : data.month,
        oilPerDay: data.totalDays > 0 ? data.totalOil / data.totalDays : 0,
        gasPerDay: data.totalDays > 0 ? data.totalGas / data.totalDays : 0
      }))
      .sort((a, b) => a.month.localeCompare(b.month));
  };

  // Generate flaring trends data
  const generateFlaringTrendsData = (productionData) => {
    const monthlyData = {};
    
    productionData.forEach(record => {
      const month = record.production_month;
      const daysInMonth = parseFloat(record.days_month) || 1;
      
      if (!monthlyData[month]) {
        monthlyData[month] = {
          month: month,
          totalFlaring: 0,
          totalGas: 0,
          totalDays: 0
        };
      }
      
      monthlyData[month].totalFlaring += parseFloat(record.flare_gas_mcf) || 0;
      monthlyData[month].totalGas += parseFloat(record.gas_mcf) || 0;
      monthlyData[month].totalDays = daysInMonth;
    });
    
    return Object.values(monthlyData)
      .map(data => ({
        month: data.month ? data.month.substring(0, 7) : data.month,
        flaringRate: data.totalDays > 0 ? data.totalFlaring / data.totalDays : 0,
        flaringIntensity: data.totalGas > 0 ? (data.totalFlaring / data.totalGas) * 100 : 0
      }))
      .sort((a, b) => a.month.localeCompare(b.month));
  };

  // Generate fuel gas trends data
  const generateFuelGasTrendsData = (productionData) => {
    const monthlyData = {};
    
    productionData.forEach(record => {
      const month = record.production_month;
      const daysInMonth = parseFloat(record.days_month) || 1;
      
      if (!monthlyData[month]) {
        monthlyData[month] = {
          month: month,
          totalFuelGas: 0,
          totalGas: 0,
          totalDays: 0
        };
      }
      
      monthlyData[month].totalFuelGas += parseFloat(record.fuel_gas_mcf) || 0;
      monthlyData[month].totalGas += parseFloat(record.gas_mcf) || 0;
      monthlyData[month].totalDays = daysInMonth;
    });
    
    return Object.values(monthlyData)
      .map(data => ({
        month: data.month ? data.month.substring(0, 7) : data.month,
        fuelGasRate: data.totalDays > 0 ? data.totalFuelGas / data.totalDays : 0,
        fuelGasIntensity: data.totalGas > 0 ? (data.totalFuelGas / data.totalGas) * 100 : 0
      }))
      .sort((a, b) => a.month.localeCompare(b.month));
  };


  const productionTrendsData = generateProductionTrendsData(productionData);
  const flaringTrendsData = generateFlaringTrendsData(productionData);
  const fuelGasTrendsData = generateFuelGasTrendsData(productionData);

  return (
    <Box sx={{ mt: 2 }}>
      {/* Chart 1: Production Trends */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Production Trends
          </Typography>
          <Tooltip title="Reset Graph" arrow>
            <IconButton
              size="small"
              color="primary"
              onClick={() => setHiddenProductionLines(new Set())}
            >
              <RestartAltIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <ResponsiveContainer {...chartConfig.containerProps}>
          <LineChart 
            data={productionTrendsData}
            margin={chartConfig.margins}
          >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="month" 
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis />
          <RechartsTooltip 
            formatter={(value, name) => {
              return [
                `${value.toLocaleString(undefined, { maximumFractionDigits: 0 })} ${name.includes('Oil') ? 'bbl/d' : 'Mcf/d'}`,
                name.includes('Oil') ? 'Oil' : 'Gas'
              ];
            }}
            labelFormatter={(label) => `Month: ${label}`}
          />
          <Legend 
            onClick={(entry) => {
              const lineType = entry.dataKey;
              setHiddenProductionLines(prev => {
                const newSet = new Set(prev);
                if (newSet.has(lineType)) {
                  newSet.delete(lineType);
                } else {
                  newSet.add(lineType);
                }
                return newSet;
              });
            }}
          />
          <Line 
            type="monotone" 
            dataKey="oilPerDay" 
            stroke="#4caf50" 
            strokeWidth={2}
            name="Oil (bbl/d)"
            dot={{ fill: '#4caf50', strokeWidth: 2, r: 4 }}
            hide={hiddenProductionLines.has('oilPerDay')}
          />
          <Line 
            type="monotone" 
            dataKey="gasPerDay" 
            stroke="#f44336" 
            strokeWidth={2}
            name="Gas (Mcf/d)"
            dot={{ fill: '#f44336', strokeWidth: 2, r: 4 }}
            hide={hiddenProductionLines.has('gasPerDay')}
          />
          </LineChart>
        </ResponsiveContainer>
      </Paper>

      {/* Chart 2: Gas Flaring Trends */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Gas Flaring Trends
          </Typography>
          <Tooltip title="Reset Graph" arrow>
            <IconButton
              size="small"
              color="primary"
              onClick={() => setHiddenFlaringLines(new Set())}
            >
              <RestartAltIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <ResponsiveContainer {...chartConfig.containerProps}>
          <LineChart 
            data={flaringTrendsData}
            margin={chartConfig.margins}
          >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="month" 
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis yAxisId="left" tick={{ fill: '#ffc107' }} />
          <YAxis yAxisId="right" orientation="right" tick={{ fill: '#8d6e63' }} />
          <RechartsTooltip 
            formatter={(value, name) => {
              if (name === 'Rate (Mcf/d)') {
                return [`${value.toLocaleString(undefined, { maximumFractionDigits: 0 })} Mcf/d`, 'Rate'];
              } else if (name === 'Intensity (%)') {
                return [`${value.toFixed(2)}%`, 'Intensity'];
              }
              return [value, name];
            }}
            labelFormatter={(label) => `Month: ${label}`}
          />
          <Legend 
            onClick={(entry) => {
              const lineType = entry.dataKey;
              setHiddenFlaringLines(prev => {
                const newSet = new Set(prev);
                if (newSet.has(lineType)) {
                  newSet.delete(lineType);
                } else {
                  newSet.add(lineType);
                }
                return newSet;
              });
            }}
          />
          <Line 
            yAxisId="left"
            type="monotone" 
            dataKey="flaringRate" 
            stroke="#ffc107" 
            strokeWidth={2}
            name="Rate (Mcf/d)"
            dot={{ fill: '#ffc107', strokeWidth: 2, r: 4 }}
            hide={hiddenFlaringLines.has('flaringRate')}
          />
          <Line 
            yAxisId="right"
            type="monotone" 
            dataKey="flaringIntensity" 
            stroke="#8d6e63" 
            strokeWidth={2}
            name="Intensity (%)"
            dot={{ fill: '#8d6e63', strokeWidth: 2, r: 4 }}
            hide={hiddenFlaringLines.has('flaringIntensity')}
          />
          </LineChart>
        </ResponsiveContainer>
      </Paper>

      {/* Chart 3: Fuel Gas Use Trends */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Fuel Gas Use Trends
          </Typography>
          <Tooltip title="Reset Graph" arrow>
            <IconButton
              size="small"
              color="primary"
              onClick={() => setHiddenFuelGasLines(new Set())}
            >
              <RestartAltIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <ResponsiveContainer {...chartConfig.containerProps}>
          <LineChart 
            data={fuelGasTrendsData}
            margin={chartConfig.margins}
          >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="month" 
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis yAxisId="left" tick={{ fill: '#2196f3' }} />
          <YAxis yAxisId="right" orientation="right" tick={{ fill: '#8d6e63' }} />
          <RechartsTooltip 
            formatter={(value, name) => {
              if (name === 'Rate (Mcf/d)') {
                return [`${value.toLocaleString(undefined, { maximumFractionDigits: 0 })} Mcf/d`, 'Rate'];
              } else if (name === 'Intensity (%)') {
                return [`${value.toFixed(2)}%`, 'Intensity'];
              }
              return [value, name];
            }}
            labelFormatter={(label) => `Month: ${label}`}
          />
          <Legend 
            onClick={(entry) => {
              const lineType = entry.dataKey;
              setHiddenFuelGasLines(prev => {
                const newSet = new Set(prev);
                if (newSet.has(lineType)) {
                  newSet.delete(lineType);
                } else {
                  newSet.add(lineType);
                }
                return newSet;
              });
            }}
          />
          <Line 
            yAxisId="left"
            type="monotone" 
            dataKey="fuelGasRate" 
            stroke="#2196f3" 
            strokeWidth={2}
            name="Rate (Mcf/d)"
            dot={{ fill: '#2196f3', strokeWidth: 2, r: 4 }}
            hide={hiddenFuelGasLines.has('fuelGasRate')}
          />
          <Line 
            yAxisId="right"
            type="monotone" 
            dataKey="fuelGasIntensity" 
            stroke="#8d6e63"
            strokeWidth={2}
            name="Intensity (%)"
            dot={{ fill: '#8d6e63', strokeWidth: 2, r: 4 }}
            hide={hiddenFuelGasLines.has('fuelGasIntensity')}
          />
          </LineChart>
        </ResponsiveContainer>
      </Paper>

      {/* Production and Gas Disposition Monthly Table */}
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 3, border: 1, borderColor: 'divider' }} data-table-title="Production and Gas Disposition Monthly" data-table-type="main">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Production and Gas Disposition Monthly
          </Typography>
          <Tooltip title="Reset Table" arrow>
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                onResetProductionMonthly();
                resetHiddenProductionLines();
                resetHiddenFlaringLines();
                resetHiddenFuelGasLines();
              }}
            >
              <RestartAltIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortProductionMonthly('month')}
                >
                  Month
                  {productionMonthlySortField === 'month' && (
                    <span style={{ marginLeft: 8 }}>
                      {productionMonthlySortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortProductionMonthly('oil_bbl_d')}
                >
                  Oil (bbl/d)
                  {productionMonthlySortField === 'oil_bbl_d' && (
                    <span style={{ marginLeft: 8 }}>
                      {productionMonthlySortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortProductionMonthly('gas_mcf_d')}
                >
                  Gas (Mcf/d)
                  {productionMonthlySortField === 'gas_mcf_d' && (
                    <span style={{ marginLeft: 8 }}>
                      {productionMonthlySortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortProductionMonthly('flaring_percent')}
                >
                  Flaring %
                  {productionMonthlySortField === 'flaring_percent' && (
                    <span style={{ marginLeft: 8 }}>
                      {productionMonthlySortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
                <TableCell 
                  align="right"
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortProductionMonthly('fuel_gas_percent')}
                >
                  Fuel Gas %
                  {productionMonthlySortField === 'fuel_gas_percent' && (
                    <span style={{ marginLeft: 8 }}>
                      {productionMonthlySortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                // Group data by production_month and calculate monthly totals and rates
                const monthlyData = {};
                
                productionData.forEach(record => {
                  const month = record.production_month;
                  const daysInMonth = parseFloat(record.days_month) || 1;
                  
                  if (!monthlyData[month]) {
                    monthlyData[month] = {
                      month: month,
                      totalOil: 0,
                      totalGas: 0,
                      totalFlareGas: 0,
                      totalFuelGas: 0,
                      totalDays: 0
                    };
                  }
                  
                  monthlyData[month].totalOil += parseFloat(record.oil_bbl) || 0;
                  monthlyData[month].totalGas += parseFloat(record.gas_mcf) || 0;
                  monthlyData[month].totalFlareGas += parseFloat(record.flare_gas_mcf) || 0;
                  monthlyData[month].totalFuelGas += parseFloat(record.fuel_gas_mcf) || 0;
                  monthlyData[month].totalDays = daysInMonth; // Use days from first record for this month
                });
                
                // Convert to array and calculate daily rates and percentages
                let monthlyArray = Object.values(monthlyData)
                  .map(data => ({
                    month: data.month ? data.month.substring(0, 7) : data.month, // Format as YYYY-MM
                    oilBblD: data.totalDays > 0 ? data.totalOil / data.totalDays : 0,
                    gasMcfD: data.totalDays > 0 ? data.totalGas / data.totalDays : 0,
                    flaringPercent: data.totalGas > 0 ? (data.totalFlareGas / data.totalGas) * 100 : 0,
                    fuelGasPercent: data.totalGas > 0 ? (data.totalFuelGas / data.totalGas) * 100 : 0
                  }));

                // Sort the data
                monthlyArray.sort((a, b) => {
                  let aValue, bValue;
                  
                  switch (productionMonthlySortField) {
                    case 'month':
                      aValue = a.month;
                      bValue = b.month;
                      break;
                    case 'oil_bbl_d':
                      aValue = a.oilBblD;
                      bValue = b.oilBblD;
                      break;
                    case 'gas_mcf_d':
                      aValue = a.gasMcfD;
                      bValue = b.gasMcfD;
                      break;
                    case 'flaring_percent':
                      aValue = a.flaringPercent;
                      bValue = b.flaringPercent;
                      break;
                    case 'fuel_gas_percent':
                      aValue = a.fuelGasPercent;
                      bValue = b.fuelGasPercent;
                      break;
                    default:
                      aValue = a.month;
                      bValue = b.month;
                  }
                  
                  if (productionMonthlySortDirection === 'asc') {
                    return aValue > bValue ? 1 : -1;
                  } else {
                    return aValue < bValue ? 1 : -1;
                  }
                });

                // Apply pagination
                const startIndex = page * rowsPerPage;
                const endIndex = startIndex + rowsPerPage;
                const paginatedData = monthlyArray.slice(startIndex, endIndex);

                return paginatedData.map((data, index) => (
                  <TableRow key={startIndex + index}>
                    <TableCell>
                      {data.month || null}
                    </TableCell>
                    <TableCell align="right">
                      {data.oilBblD ? data.oilBblD.toFixed(0) : null}
                    </TableCell>
                    <TableCell align="right">
                      {data.gasMcfD ? data.gasMcfD.toFixed(0) : null}
                    </TableCell>
                    <TableCell align="right">
                      {data.flaringPercent ? data.flaringPercent.toFixed(2) : null}
                    </TableCell>
                    <TableCell align="right">
                      {data.fuelGasPercent ? data.fuelGasPercent.toFixed(2) : null}
                    </TableCell>
                  </TableRow>
                ));
              })()}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        {(() => {
          // Calculate total rows for pagination
          const monthlyData = {};
          productionData.forEach(record => {
            const month = record.production_month;
            if (!monthlyData[month]) {
              monthlyData[month] = true;
            }
          });
          const totalRows = Object.keys(monthlyData).length;
          
          return (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={totalRows}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Rows:"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
              sx={{
                '& .MuiTablePagination-toolbar': {
                  justifyContent: 'center',
                },
                '& .MuiTablePagination-spacer': {
                  display: 'none',
                },
              }}
            />
          );
        })()}
      </Paper>
    </Box>
  );
};

export default ProductionCharts;